import { useEffect, useReducer, useState } from "react"
import { useSearchParams } from "react-router"

const VIDEO_STATE = {
  MISSING_HASH: "missing_hash",
  CHECKING: "checking",
  PROCESSING: "processing",
  READY: "ready",
}

const initialState = {
  status: VIDEO_STATE.CHECKING,
  cachedVideoBlob: null,
  isCaching: false,
}

function videoReducer(state, action) {
  switch (action.type) {
    case "HASH_MISSING":
      return {
        status: VIDEO_STATE.MISSING_HASH,
        cachedVideoBlob: null,
        isCaching: false,
      }
    case "HASH_CHANGED":
      return {
        status: VIDEO_STATE.CHECKING,
        cachedVideoBlob: null,
        isCaching: false,
      }
    case "VIDEO_READY":
      return {
        ...state,
        status: VIDEO_STATE.READY,
        cachedVideoBlob: action.blob,
        isCaching: false,
      }
    case "VIDEO_PENDING":
      return {
        ...state,
        status: VIDEO_STATE.PROCESSING,
      }
    case "CACHE_STARTED":
      return {
        ...state,
        cachedVideoBlob: null,
        isCaching: true,
      }
    default:
      return state
  }
}

async function fetchVideoBlob(videoUrl, { signal } = {}) {
  const response = await fetch(videoUrl, { signal })

  if (!response.ok) {
    throw new Error(`Failed to cache video: ${response.status}`)
  }

  return response.blob()
}

export function useVideoDownload() {
  const [state, dispatch] = useReducer(videoReducer, initialState)
  const [cachedVideoUrl, setCachedVideoUrl] = useState(null)
  const [searchParams] = useSearchParams()
  const urlHash = searchParams.get("h")
  const videoPreviewUrl = urlHash
    ? `https://holobooth.hypervsn.com/video/${urlHash}.mp4`
    : null

  useEffect(() => {
    if (videoPreviewUrl) {
      dispatch({ type: "HASH_CHANGED" })
      return
    }

    dispatch({ type: "HASH_MISSING" })
  }, [videoPreviewUrl])

  useEffect(() => {
    if (!state.cachedVideoBlob) {
      setCachedVideoUrl(null)
      return
    }

    const objectUrl = window.URL.createObjectURL(state.cachedVideoBlob)
    setCachedVideoUrl(objectUrl)

    return () => {
      window.URL.revokeObjectURL(objectUrl)
    }
  }, [state.cachedVideoBlob])

  useEffect(() => {
    if (!videoPreviewUrl) return

    const controller = new AbortController()
    let retryTimeoutId = null

    const loadVideo = () => {
      dispatch({ type: "CACHE_STARTED" })

      fetchVideoBlob(videoPreviewUrl, { signal: controller.signal })
        .then((blob) => {
          if (controller.signal.aborted) return
          dispatch({ type: "VIDEO_READY", blob })
        })
        .catch((error) => {
          if (controller.signal.aborted) return
          console.error("Error loading video:", error)
          dispatch({ type: "VIDEO_PENDING" })
          retryTimeoutId = setTimeout(loadVideo, 10000)
        })
    }

    loadVideo()

    return () => {
      controller.abort()
      if (retryTimeoutId) clearTimeout(retryTimeoutId)
    }
  }, [videoPreviewUrl])

  const page =
    state.status === VIDEO_STATE.MISSING_HASH
      ? "empty"
      : state.status === VIDEO_STATE.PROCESSING
        ? "processing"
        : "default"

  return {
    cachedVideoBlob: state.cachedVideoBlob,
    cachedVideoUrl,
    isCaching: state.isCaching,
    isLoading: state.status === VIDEO_STATE.CHECKING,
    page,
    urlHash,
    videoPreviewUrl,
  }
}
